import React, { useState, useEffect } from 'react';
import { Table, Typography, Input, Space, Button, Modal, Descriptions, message, Form, Input as FormInput, Select, Checkbox } from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import './ViewList.css';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const ViewList = ({ type }) => {
  const [dataSource, setDataSource] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [editForm] = Form.useForm();
  const [addForm] = Form.useForm();

  // Fetch users from backend
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch('/backend/superadmin_get_users.php', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('Erreur de parsing JSON:', e, 'Texte reçu:', text);
        throw new Error('Réponse non-JSON');
      }
      if (response.ok) {
        console.log('Données utilisateurs reçues:', data.users);
        setDataSource(data.users || []);
        setFilteredData(data.users || []);
      } else {
        if (response.status === 403) {
          message.error('Session expirée. Veuillez vous reconnecter.');
        } else {
          message.error(data.error || `Erreur HTTP ${response.status}`);
        }
      }
    } catch (error) {
      console.error('Erreur de réseau:', error.message);
      message.error('Erreur réseau lors de la récupération des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (type === 'users') {
      fetchUsers();
    }
  }, [type]);

  // Handle search
  const onSearch = (value) => {
    const filtered = dataSource.filter((item) =>
      Object.values(item).some((val) =>
        val ? val.toString().toLowerCase().includes(value.toLowerCase()) : false
      )
    );
    setFilteredData(filtered);
  };

  // Handle view
  const handleView = (record) => {
    setSelectedUser(record);
    setIsViewModalVisible(true);
  };

  // Handle edit
  const handleEdit = (record) => {
    setSelectedUser(record);
    editForm.setFieldsValue({
      first_name: record.first_name,
      last_name: record.last_name,
      email: record.email,
      is_confirmed: !!record.is_confirmed,
      gender: record.gender,
      city: record.city,
      zip_code: record.zip_code,
      main_phone: record.main_phone,
    });
    setIsEditModalVisible(true);
  };

  // Handle edit form submission
  const handleEditSubmit = async () => {
    try {
      const values = await editForm.validateFields();
      const response = await fetch('/backend/superadmin_update_user.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id: selectedUser.id, ...values }),
      });
      const data = await response.json();
      if (response.ok) {
        message.success(data.message);
        setIsEditModalVisible(false);
        editForm.resetFields();
        setSelectedUser(null);
        fetchUsers();
      } else {
        message.error(data.error || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors de la mise à jour de l\'utilisateur');
    }
  };

  // Handle delete
  const handleDelete = (record) => {
    console.log('handleDelete appelé pour:', record);
    setUserToDelete(record);
    setIsDeleteModalVisible(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    console.log('Confirmation de suppression pour ID:', userToDelete.id);
    try {
      const response = await fetch('/backend/superadmin_delete_user.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id: userToDelete.id }),
      });
      console.log('Réponse de suppression:', response.status, response.statusText);
      const data = await response.json();
      console.log('Données de réponse:', data);
      if (response.ok) {
        message.success(data.message || 'Utilisateur supprimé avec succès');
        setIsDeleteModalVisible(false);
        setUserToDelete(null);
        fetchUsers();
      } else {
        message.error(data.error || `Erreur HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Erreur réseau lors de la suppression:', error);
      message.error('Erreur réseau lors de la suppression');
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    console.log('Suppression annulée');
    setIsDeleteModalVisible(false);
    setUserToDelete(null);
  };

  // Handle add user
  const handleAdd = () => {
    setIsAddModalVisible(true);
  };

  // Handle add form submission
  const handleAddSubmit = async () => {
    try {
      const values = await addForm.validateFields();
      const response = await fetch('/backend/superadmin_add_user.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(values),
      });
      const data = await response.json();
      if (response.ok) {
        message.success(data.message);
        setIsAddModalVisible(false);
        addForm.resetFields();
        fetchUsers();
      } else {
        message.error(data.error || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors de la création de l\'utilisateur');
    }
  };

  // Handle modal close
  const handleViewModalOk = () => {
    setIsViewModalVisible(false);
    setSelectedUser(null);
  };

  const handleEditModalCancel = () => {
    setIsEditModalVisible(false);
    editForm.resetFields();
    setSelectedUser(null);
  };

  const handleAddModalCancel = () => {
    setIsAddModalVisible(false);
    addForm.resetFields();
  };

  // Define columns for users
  const userColumns = [
    {
      title: 'Name',
      dataIndex: 'first_name',
      key: 'first_name',
      sorter: (a, b) => a.first_name.localeCompare(b.first_name),
      render: (_, record) => `${record.first_name} ${record.last_name}`,
    },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Confirmed',
      dataIndex: 'is_confirmed',
      key: 'is_confirmed',
      render: (is_confirmed) => (is_confirmed ? 'Yes' : 'No'),
    },
    {
      title: 'Status',
      dataIndex: 'Status',
      key: 'Status',
      render: (status) => (
        <span className={status === 'Active' ? 'status-active' : 'status-inactive'}>{status}</span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button
            icon={<EyeOutlined />}
            type="primary"
            size="small"
            onClick={() => handleView(record)}
          >
            View
          </Button>
          <Button
            icon={<EditOutlined />}
            type="primary"
            size="small"
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            icon={<DeleteOutlined />}
            danger
            size="small"
            onClick={() => handleDelete(record)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  // Define columns for service providers (placeholder)
  const serviceProviderColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name', sorter: (a, b) => a.name.localeCompare(b.name) },
    { title: 'Category', dataIndex: 'category', key: 'category' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'IP Address', dataIndex: 'ip', key: 'ip' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <span className={status === 'Active' ? 'status-active' : 'status-inactive'}>{status}</span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button
            icon={<EditOutlined />}
            type="primary"
            size="small"
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            icon={<DeleteOutlined />}
            danger
            size="small"
            onClick={() => handleDelete(record)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  // Determine columns based on type
  const columns = type === 'users' ? userColumns : serviceProviderColumns;

  return (
    <div className="view-list-container">
      <Title level={3} className="view-list-title">
        {type === 'users' ? 'User List' : 'Service Provider List'}
      </Title>
      <div className="view-list-header">
        <Space>
          <Search
            placeholder="Search..."
            onSearch={onSearch}
            style={{ width: 300 }}
            enterButton
          />
          {type === 'users' && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              Add User
            </Button>
          )}
        </Space>
      </div>
      <Table
        dataSource={filteredData}
        columns={columns}
        pagination={{ pageSize: 5 }}
        className="view-list-table"
        loading={loading}
      />
      {/* View Modal */}
      <Modal
        title={`User Details - ${selectedUser?.first_name} ${selectedUser?.last_name}`}
        open={isViewModalVisible}
        onOk={handleViewModalOk}
        onCancel={handleViewModalOk}
        okText="Close"
        cancelButtonProps={{ style: { display: 'none' } }}
        width={800}
      >
        {selectedUser && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="ID">{selectedUser.id}</Descriptions.Item>
            <Descriptions.Item label="First Name">{selectedUser.first_name}</Descriptions.Item>
            <Descriptions.Item label="Last Name">{selectedUser.last_name}</Descriptions.Item>
            <Descriptions.Item label="Email">{selectedUser.email}</Descriptions.Item>
            <Descriptions.Item label="Confirmed">{selectedUser.is_confirmed ? 'Yes' : 'No'}</Descriptions.Item>
            <Descriptions.Item label="Status">{selectedUser.Status}</Descriptions.Item>
            <Descriptions.Item label="Created At">{selectedUser.created_at}</Descriptions.Item>
            <Descriptions.Item label="Gender">{selectedUser.gender || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Household Type">{selectedUser.household_type || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Street">{selectedUser.street || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="City">{selectedUser.city || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Zip Code">{selectedUser.zip_code || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="County">{selectedUser.county || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="State">{selectedUser.state || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Country">{selectedUser.country || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Housing Status">{selectedUser.housing_status || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Profile Picture">
              {selectedUser.profile_picture ? (
                <a href={`/backend/${selectedUser.profile_picture}`} target="_blank" rel="noopener noreferrer">
                  View
                </a>
              ) : (
                'N/A'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Veteran Status">{selectedUser.veteran_status || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Veteran Service">{selectedUser.veteran_service || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Branch of Military">{selectedUser.branch_of_military || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Military Operations">{selectedUser.military_operations || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Military Discharge Status">{selectedUser.military_discharge_status || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Military Service Start">{selectedUser.military_service_start || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Military Service End">{selectedUser.military_service_end || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Support System">{selectedUser.support_system || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Record Type">{selectedUser.record_type || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Housing Loss City">{selectedUser.housing_loss_city || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Housing Loss Zip Code">{selectedUser.housing_loss_zip_code || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Connect to Services">{selectedUser.connect_to_services || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Preferred Housing Resources">{selectedUser.preferred_housing_resources || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="City to Receive Services">{selectedUser.city_to_receive_services || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Most Time Location">{selectedUser.most_time_location || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Fully Private">{selectedUser.fully_private ? 'Yes' : 'No'}</Descriptions.Item>
            <Descriptions.Item label="Selected Providers">{selectedUser.selected_providers ? 'Yes' : 'No'}</Descriptions.Item>
            <Descriptions.Item label="General Providers">{selectedUser.general_providers ? 'Yes' : 'No'}</Descriptions.Item>
            <Descriptions.Item label="Research">{selectedUser.research ? 'Yes' : 'No'}</Descriptions.Item>
            <Descriptions.Item label="Main Phone">{selectedUser.main_phone || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Other Contact">{selectedUser.other_contact || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Preferred Contact Method">{selectedUser.preferred_contact_method || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Contact Person Name">{selectedUser.contact_person_name || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Contact Details">{selectedUser.contact_details || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Contact Type">{selectedUser.contact_type || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Staff Name">{selectedUser.staff_name || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Staff Contact">{selectedUser.staff_contact || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Additional Staff Contact">{selectedUser.additional_staff_contact || 'N/A'}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
      {/* Edit Modal */}
      <Modal
        title="Edit User"
        open={isEditModalVisible}
        onOk={handleEditSubmit}
        onCancel={handleEditModalCancel}
        okText="Save"
        cancelText="Cancel"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="first_name"
            label="First Name"
            rules={[{ required: true, message: 'Please enter first name' }]}
          >
            <FormInput />
          </Form.Item>
          <Form.Item
            name="last_name"
            label="Last Name"
            rules={[{ required: true, message: 'Please enter last name' }]}
          >
            <FormInput />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, type: 'email', message: 'Please enter a valid email' }]}
          >
            <FormInput />
          </Form.Item>
          <Form.Item name="is_confirmed" valuePropName="checked">
            <Checkbox>Confirmed</Checkbox>
          </Form.Item>
          <Form.Item name="gender" label="Gender">
            <Select allowClear>
              <Option value="Male">Male</Option>
              <Option value="Female">Female</Option>
              <Option value="Other">Other</Option>
            </Select>
          </Form.Item>
          <Form.Item name="city" label="City">
            <FormInput />
          </Form.Item>
          <Form.Item name="zip_code" label="Zip Code">
            <FormInput />
          </Form.Item>
          <Form.Item name="main_phone" label="Main Phone">
            <FormInput />
          </Form.Item>
        </Form>
      </Modal>
      {/* Add Modal */}
      <Modal
        title="Add User"
        open={isAddModalVisible}
        onOk={handleAddSubmit}
        onCancel={handleAddModalCancel}
        okText="Create"
        cancelText="Cancel"
      >
        <Form form={addForm} layout="vertical">
          <Form.Item
            name="first_name"
            label="First Name"
            rules={[{ required: true, message: 'Please enter first name' }]}
          >
            <FormInput />
          </Form.Item>
          <Form.Item
            name="last_name"
            label="Last Name"
            rules={[{ required: true, message: 'Please enter last name' }]}
          >
            <FormInput />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, type: 'email', message: 'Please enter a valid email' }]}
          >
            <FormInput />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: 'Please enter a password', min: 6 }]}
          >
            <FormInput.Password />
          </Form.Item>
          <Form.Item name="is_confirmed" valuePropName="checked">
            <Checkbox>Confirmed</Checkbox>
          </Form.Item>
          <Form.Item name="gender" label="Gender">
            <Select allowClear>
              <Option value="Male">Male</Option>
              <Option value="Female">Female</Option>
              <Option value="Other">Other</Option>
            </Select>
          </Form.Item>
          <Form.Item name="city" label="City">
            <FormInput />
          </Form.Item>
          <Form.Item name="zip_code" label="Zip Code">
            <FormInput />
          </Form.Item>
          <Form.Item name="main_phone" label="Main Phone">
            <FormInput />
          </Form.Item>
        </Form>
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirmer la suppression"
        open={isDeleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Supprimer"
        okType="danger"
        cancelText="Annuler"
      >
        <p>
          Voulez-vous vraiment supprimer l'utilisateur {userToDelete?.first_name} {userToDelete?.last_name} et toutes ses informations ?
        </p>
      </Modal>
    </div>
  );
};

export default ViewList;